import AuthService from "@/services/AuthService";
import AxiosInstance from "@/services/AxiosInstance";
import CompaniesService from "@/services/CompaniesService";
import UsersService from "@/services/UsersService";

import CodeError from "@/utils/error";
import Encrypt from "@/utils/encrypt";
import router from "@/router";

const getDefaultState = () => ({
    token: localStorage.getItem("token") || null,
    loggedUser: null,
    autoLogin: false,
});

export default {
    state: () => getDefaultState(),
    getters: {
        getToken: state => state.token,
        isAuthenticated: state => state.token !== null,
        isLogged: state => state.loggedUser !== null,
        getLoggedUserID: state => state.loggedUser?.id,
        getLoggedUserldapLogin: state => state.loggedUser?.ldap_login,
        getLoggedUserCompany: state => state.loggedUser?.company_name,
        getLoggedUserGroup: state => state.loggedUser?.group,
        getLoggedUserName: state => state.loggedUser?.name,
        getLoggedUserEmail: state => state.loggedUser?.email,
        getAutoLogin: state => state.autoLogin,
    },
    mutations: {
        setUser: (state, user) => {
            state.loggedUser = user;
        },
        setToken: (state, token) => {
            state.token = token;
            if (!token) {
                Object.assign(AxiosInstance.defaults, {
                    headers: {
                        Authorization:
                            "84514bff5ee37401bfe11f1321f7810149f40e448c7c6431bb83dffd104a4912",
                        "Relay-Authorization-Type": "ldap",
                        "Content-Type": "application/json",
                    },
                });
                localStorage.removeItem("token");
                // Go to login screen
                router.push("login").catch(() => {});
            } else {
                Object.assign(AxiosInstance.defaults, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                localStorage.setItem("token", token);
            }
        },
        setAutoLogin: (state, autoLogin) => {
            state.autoLogin = autoLogin;
        },
        setAuthState: state => {
            Object.assign(state, getDefaultState());
        },
    },
    actions: {
        isLoggedIn: async ({ dispatch, commit, getters }) => {
            try {
                const token = getters.getToken;
                const { unm } = JSON.parse(atob(token.split(".")[1]));
                commit("setToken", token);
                await dispatch("getUser", unm);
            } catch (err) {
                dispatch("logout");
            }
        },
        login: async ({ dispatch, commit }, authData) => {
            try {
                const user = `${authData.username}:${authData.password}`;
                const base64Credentials = Encrypt(user, "f323ee5571a5931a");
                const response = await AuthService.getToken(base64Credentials);
                const token = response.data;
                commit("setToken", token);
                return dispatch("getUser", authData.username);
            } catch (e) {
                commit("setToken", null);
                router.push("login").catch(() => {});
                if (e.response) {
                    if (
                        e.response.status &&
                        e.response.status === 401 &&
                        e.response.data.data &&
                        e.response.data.data.authenticated &&
                        !e.response.data.data.has_profile
                    ) {
                        return dispatch(
                            "createDefaultProfileEntry",
                            authData.username
                        );
                    }
                    throw new CodeError(
                        "Failed to login. Please check your credentials.",
                        e?.response?.status || 400,
                        e
                    );
                }
                throw new CodeError("Failed to login. Network error.", 403, e);
            }
        },

        logout: ({ dispatch }) => {
            // Reset state
            dispatch("resetAuthState");
            dispatch("resetCompaniesState");
            dispatch("resetDriverOverviewState");
            dispatch("resetHomeState");
            dispatch("resetRecurringRegistrationsState");
            dispatch("resetRegistrationsState");
            dispatch("resetRegistrationsArchiveState");
            dispatch("resetTransportersState");
            dispatch("resetUsersState");
        },

        refreshToken: async ({ dispatch, commit }) => {
            try {
                commit("setLoading", true);
                const response = await AuthService.renewToken();
                const token = response.data;
                commit("setToken", token);
            } catch (e) {
                commit("setToken", null);
                dispatch("generalErrorHandling", e);
            } finally {
                commit("setLoading", false);
            }
        },
        refreshInfo: ({ dispatch }) => {
            const requests = [
                dispatch("processRegistrations"),
                dispatch("processAllDrivers"),
            ];
            const optionalRequests = (() => {
                switch (router.currentRoute.path) {
                    case "/home":
                        return ["processHome"];
                    case "/company":
                        return ["processCompanies"];
                    case "/users":
                        return ["processUsers", "processCompanies"];
                    case "/registrations":
                        return ["processCompanies", "processTransporter"];
                    case "/registrations-archive":
                        return ["processArchive"];
                    case "/recurring-registrations":
                        return ["processRecRegistrations"];
                    case "/driver-overview":
                        return ["processDriversByDate"];
                    default:
                        return [];
                }
            })();
            for (const optionalRequest of optionalRequests) {
                requests.push(dispatch(optionalRequest));
            }
            return Promise.all(requests);
        },

        createDefaultProfileEntry: async ({ commit }, username) => {
            try {
                const response = await CompaniesService.get();
                const companies = response.data.data;
                const matchedCompany = companies.find(
                    company => company.name === "CTS GROUP"
                );
                if (!matchedCompany) {
                    throw new CodeError(
                        "User is authorized but has no registered profile. Company not found.",
                        404
                    );
                }
                const names = username
                    .split(".")
                    .map(name => name.charAt(0).toUpperCase() + name.slice(1))
                    .join(" ");
                const defaultUser = {
                    ldap_login: username,
                    name: names,
                    company_id: matchedCompany.id,
                    email: `${username}@ctsgroup.nl`,
                    phone: "0252628900",
                    group: "FT2",
                };
                await UsersService.create(defaultUser);
                commit("setAutoLogin", true);
            } catch (error) {
                const errorStatus = error?.response?.status || 400;
                const errorURL = error?.response?.config?.url?.split("/").pop();
                if (errorURL === "company") {
                    // POST to /company endpoint failed
                    throw new CodeError(
                        "User is authorized but has no registered profile. An error occurred while trying to assign the user to a default company.",
                        errorStatus,
                        error
                    );
                } else if (errorURL === "users") {
                    // POST to /users endpoint failed
                    throw new CodeError(
                        "User is authorized but has no registered profile. An error occurred while trying to add a defult profile.",
                        errorStatus,
                        error
                    );
                } else {
                    throw new CodeError(
                        "User is authorized but has no registered profile. An error occurred while trying to build a default profile.",
                        errorStatus,
                        error
                    );
                }
            }
        },

        resetAuthState: ({ commit }) => {
            commit("setToken", null);
            commit("setAuthState");
        },
    },
};
